import { Link } from '@tanstack/react-router';
import { ArrowLeft, Heart } from 'lucide-react';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { PageLayout } from '@/components/PageLayout';
import { InArticleAd, BannerAd } from '@/components/AdComponents';
import { trackEvent } from '@/config/analytics';
import { Meta } from '@/components/MetaManager';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';
import { EntityAnchor } from '@/components/EntityAnchor';

export default function AboutTrauma() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const lang = i18n.language as 'en' | 'zh';

  // 跟踪页面访问
  const handleCTAClick = () => {
    trackEvent('trauma_cta_click', {
      page: 'about_trauma',
      section: 'cta_button'
    });
  };

  const breadcrumbs = generateBreadcrumbs([
    { label: lang === 'zh' ? '关于我' : 'About', path: '/about' },
    { label: lang === 'zh' ? '创伤探索者' : 'Trauma Explorer', path: '/about/trauma' }
  ], lang);

  return (
    <PageLayout>
      <Meta
        title={`${t.about.trauma.title} | borninsea`}
        description={t.about.trauma.subtitle}
        keywords="创伤疗愈,心理健康,自我成长,原生家庭,情感疗愈"
      />
      <StructuredData type="profile" breadcrumbs={breadcrumbs} />
      <div className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* 页面顶部横幅广告 */}
          <BannerAd className="mb-8" />

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-full bg-rose-500/20 border border-rose-500/30">
                <Heart className="w-8 h-8 text-rose-400" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">{t.about.trauma.title}</h1>
                <p className="text-muted-foreground">{t.about.trauma.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg prose-invert max-w-none">
            <p className="text-xl text-muted-foreground mb-8">{t.about.trauma.greeting}</p>
            
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-rose-300">{t.about.trauma.sections.beginning.title}</h2>
              <div className="space-y-4">
                {t.about.trauma.sections.beginning.content.map((paragraph, index) => (
                  <p key={index} className={index === 1 ? "text-foreground/80 font-medium" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            {/* 第一个实体锚点 - beginning section 后 */}
            <EntityAnchor lang={lang} variant="subtle" className="my-8" />

            {/* 文章中间广告 */}
            <InArticleAd />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-rose-300">{t.about.trauma.sections.slowStart.title}</h2>
              <div className="space-y-4">
                {t.about.trauma.sections.slowStart.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </section>

            {/* 第二个实体锚点 - slowStart section 后 */}
            <EntityAnchor lang={lang} variant="inline" showIdentities className="my-6" />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-rose-300">{t.about.trauma.sections.silence.title}</h2>
              <div className="space-y-4">
                {t.about.trauma.sections.silence.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
                <ul className="list-none space-y-2 text-foreground/80 my-4">
                  {t.about.trauma.sections.silence.list.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
                {t.about.trauma.sections.silence.conclusion.map((paragraph, index) => (
                  <p key={index} className={index === 1 ? "text-foreground font-medium" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            {/* 第三个实体锚点 - silence section 后，完整版带指标 */}
            <EntityAnchor lang={lang} variant="block" full showMetrics className="my-8" />
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <Link
              to={getLocalizedPath('/trauma')}
              onClick={handleCTAClick}
              className="inline-flex items-center gap-2 px-8 py-4 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-foreground rounded-xl transition-all duration-300"
            >
              {t.about.trauma.sections.cta}
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </div>

          {/* 页面底部广告 */}
          <BannerAd className="mt-12" />
        </div>
      </div>
    </PageLayout>
  );
}
